import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
// @mui
import {
  Box,
  List,
  Badge,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemButton,
  CircularProgress,
  Button,
  useTheme,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { useDispatch, useSelector } from '../../../redux/store';
import { getInbox, getUnreadInboxCount, setUnreadInboxCount } from '../../../redux/slices/lookup';
import useInterval from '../../../hooks/useInterval';
import LinearLoader from '../../../components/LinearLoader';
import useLocales from '../../../hooks/useLocales';
import Image from '../../../components/Image';

// ----------------------------------------------------------------------

export default function NotificationPopover({ withLabel = true }) {
  const theme = useTheme();

  const [isHover, setIsHover] = useState(false);

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const { unreadInboxCount, inbox, memberBalance } = useSelector((x) => x.lookup);

  const [isLoading, setIsLoading] = useState(false);

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setIsLoading(true);
    getInbox().finally(() => {
      setIsLoading(false);
      dispatch(setUnreadInboxCount(0));
    });
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const unreadMessages = useMemo(() => inbox.filter((m) => !m.read), [inbox]);
  const readMessages = useMemo(() => inbox.filter((m) => m.read), [inbox]);

  useInterval(getUnreadInboxCount, 15000);

  return (
    <>
      <Box
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        sx={{
          cursor: { xs: 'default', md: 'pointer' }, // pointer only on desktop

          // Mobile styles (xs)
          borderRadius: { xs: '50%', md: 0 },
          background: {
            xs:
              'linear-gradient(#1A1A1A, #1A1A1A) padding-box, ' +
              'linear-gradient(180deg, #FDD146 0%, #F09F0E 100%) border-box',
            md: 'none',
          },
          border: { xs: '1.2px solid transparent', md: 'none' },
          width: { xs: '8vw', md: 'auto' },
          height: { xs: '8vw', md: 'auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          '&:hover': {
            opacity: { xs: 1, md: 0.8 },
          },
        }}
      >
        <Badge badgeContent={unreadInboxCount || 0} color="error" sx={{ display: 'flex', alignItems: 'center' }}>
          {/* 🔔 ICON SWAP ON HOVER */}
          {isHover ? (
            <Image src={require('../../../assets/home/bell-hover.png')} style={{ height: '20px', width: '20px' }} />
          ) : (
            <Image src={require('../../../assets/home/bell.png')} style={{ height: '20px', width: '20px' }} />
          )}

          {/* 📝 TEXT HOVER COLOR */}
          {withLabel && (
            <Typography
              fontSize="12px"
              sx={{
                ml: 0.5,
                color: isHover ? '#F1C40F' : '#fff',
                transition: '0.2s',
                display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
              }}
            >
              {translate('notifications')}
            </Typography>
          )}
        </Badge>
      </Box>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 360,
            mt: 1.5,
            ml: 0.75,
            borderRadius: '12px',
            border: '2px solid transparent',
            backgroundImage: `linear-gradient(${theme.palette.goldcity.background.main}, ${theme.palette.goldcity.background.main}), 
                        linear-gradient(180deg, #FDD146 0%, #F09F0E 100%)`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',

            // 🔥 IMPORTANT FIX
            maxHeight: 500,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {isLoading ? (
          <Box sx={{ p: 1, height: 100 }}>
            <LinearLoader />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1">{translate('notifications')}</Typography>
                <Typography variant="body2" sx={{ color: '#FF9D00' }}>
                  {translate('you_have_x_unread_messages', { x: unreadInboxCount })}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderStyle: 'dashed' }} />

            {/* ▼▼▼ HIDDEN SCROLLBAR ▼▼▼ */}
            <Scrollbar
              sx={{
                maxHeight: 340,
                overflowY: 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {unreadMessages?.length ? (
                <List
                  disablePadding
                  subheader={
                    <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                      {translate('new')}
                    </ListSubheader>
                  }
                >
                  {unreadMessages.map((message) => (
                    <NotificationItem
                      key={message.id}
                      notification={message}
                      currencyCode={memberBalance?.currencyCode}
                    />
                  ))}
                </List>
              ) : null}

              {readMessages?.length ? (
                <List
                  disablePadding
                  subheader={
                    unreadMessages?.length ? (
                      <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                        Before that
                      </ListSubheader>
                    ) : null
                  }
                >
                  {readMessages.map((message) => (
                    <NotificationItem
                      key={message.id}
                      notification={message}
                      currencyCode={memberBalance?.currencyCode}
                    />
                  ))}
                </List>
              ) : null}
            </Scrollbar>
          </>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* <Box sx={{ p: 1 }}>
          <Button fullWidth>View All</Button>
        </Box> */}
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    created: PropTypes.instanceOf(Date),
    id: PropTypes.string,
    read: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    activity_type: PropTypes.string,
  }),
};

function NotificationItem({ notification }) {
  const { title } = renderContent(notification);

  return (
    <ListItemButton
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(!notification.read && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fToNow(notification.created)}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = <Typography variant="subtitle2">{notification?.title}</Typography>;

  return { title };
}
